import React, { useState } from 'react';
import { Alert, Checkbox, Form, Input, Modal } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/api/queryKeys';
import {
  ProductImageApiError,
  productImages,
} from '@/api/ProductImages/ProductImages';
import { useTranslate } from '@/translations';

export const SuspendImageDialog = ({
  productId,
  revisionId,
  onClose,
}: {
  productId: number;
  revisionId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslate();
  const client = useQueryClient();
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<number | null>(null);
  const submit = async () => {
    if (
      submitting ||
      !reason.trim() ||
      !confirmed ||
      error === 403 ||
      error === 409
    )
      return;
    setSubmitting(true);
    setError(null);
    try {
      await productImages.suspend(productId, revisionId, {
        expectedCurrentRevisionId: revisionId,
        reason: reason.trim(),
      });
      await client.invalidateQueries({ queryKey: [QUERY_KEYS.productImagery] });
      onClose();
    } catch (failure) {
      setError(failure instanceof ProductImageApiError ? failure.status : 500);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Modal
      open
      centered
      title={t('imagery.suspendTitle')}
      onCancel={submitting ? undefined : onClose}
      onOk={() => void submit()}
      confirmLoading={submitting}
      okText={t('imagery.confirmSuspension')}
      cancelText={t('imagery.cancel')}
      okButtonProps={{
        danger: true,
        disabled:
          !reason.trim() || !confirmed || error === 403 || error === 409,
      }}
    >
      <p>{t('imagery.suspendDescription')}</p>
      <Form layout="vertical">
        <Form.Item
          label={t('imagery.reason')}
          htmlFor="imagery-suspension-reason"
          required
        >
          <Input.TextArea
            id="imagery-suspension-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={1000}
            rows={3}
            disabled={submitting}
          />
        </Form.Item>
        <Checkbox
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
          disabled={submitting}
        >
          {t('imagery.suspendAcknowledge')}
        </Checkbox>
      </Form>
      {error ? (
        <Alert
          className="mt-3"
          type="error"
          showIcon
          message={t(
            error === 409
              ? 'imagery.suspendConflict'
              : error === 403
                ? 'imagery.suspendDenied'
                : 'imagery.suspendFailed',
          )}
        />
      ) : null}
    </Modal>
  );
};
